export type SseFrame = {
  event: string
  data: unknown
}

/**
 * Parses a Server-Sent Events response body into typed frames.
 *
 * Each yielded frame has an `event` name (defaulting to "message" per the SSE
 * spec) and the JSON-parsed `data` payload. Comment lines (`:` prefix) and
 * frames without a `data:` field are skipped.
 *
 * The generator returns when the stream closes. Aborting the underlying
 * `fetch` (via the request's AbortSignal) is the supported cancellation path;
 * the reader will resolve to `{ done: true }`.
 */
export async function* parseSseStream(
  body: ReadableStream<Uint8Array>,
): AsyncGenerator<SseFrame, void, void> {
  const reader = body.getReader()
  const decoder = new TextDecoder("utf-8")
  let buffer = ""

  try {
    while (true) {
      const { value, done } = await reader.read()
      if (done) {
        if (buffer.trim().length > 0) {
          const frame = parseFrame(buffer)
          if (frame) yield frame
        }
        return
      }
      buffer += decoder.decode(value, { stream: true })

      let sep: number
      while ((sep = buffer.indexOf("\n\n")) !== -1) {
        const raw = buffer.slice(0, sep)
        buffer = buffer.slice(sep + 2)
        const frame = parseFrame(raw)
        if (frame) yield frame
      }
    }
  } finally {
    reader.releaseLock()
  }
}

function parseFrame(raw: string): SseFrame | null {
  let event = "message"
  const dataLines: string[] = []
  for (const line of raw.split("\n")) {
    if (line.length === 0 || line.startsWith(":")) continue
    const idx = line.indexOf(":")
    const field = idx === -1 ? line : line.slice(0, idx)
    const value =
      idx === -1 ? "" : line.slice(idx + 1).replace(/^ /, "")
    if (field === "event") event = value
    else if (field === "data") dataLines.push(value)
  }
  if (dataLines.length === 0) return null
  try {
    return { event, data: JSON.parse(dataLines.join("\n")) }
  } catch {
    return null
  }
}
